<template>
  <v-layout column fill-height>

    <!-- Top / scrollable menu area -->
    <v-container class="no-margins d-flex flex-column flex-grow-1">
      <template v-if="isLoggedIn && isRegistered">
        <div class="user-details">
          <v-icon size="20" class="user-details__icon">$sl-user</v-icon>

          <p class="user-details__name">
            {{ currentUser.firstName + ' ' + currentUser.lastName }}
          </p>
        </div>
      </template>

      <!-- Everything except logout -->
      <div class="side-menu-content flex-grow-1">

        <v-list nav dense class="mainmenu">

          <!-- Main menu items via loop -->
          <template>
            <div>
              <template v-for="(item, index) in mainMenuItems">

                <v-list
                  v-if="item.group && item.children"
                  :key="`main-list-${index}`"
                  nav
                  dense
                  flat
                  class="pa-0 border-0 no-border-list"
                >
                  <v-list-group
                    :value="openGroups[index]"
                    @input="(val) => $set(openGroups, index, val)"
                    @click="item.onClick ? item.onClick() : null"
                  >
                    <template v-slot:activator>
                      <v-list-item-icon v-if="item.icon">
                        <v-icon>{{ item.icon }}</v-icon>
                      </v-list-item-icon>

                      <v-list-item-content style="position: relative;">
                        <v-list-item-title class="text-truncate" style="padding-right: 28px;">
                          {{ item.label }}
                        </v-list-item-title>
                        <v-badge
                          v-if="item.badge && !openGroups[index]"
                          inline
                          color="red"
                          :content="item.badge.content"
                          :value="item.badge.value"
                          style="position: absolute; top: 0; right: 0;"
                        />
                      </v-list-item-content>
                    </template>

                    <template v-for="(child, childIndex) in item.children">

                      <v-list-group
                        v-if="child.subGroup && child.children"
                        :key="`main-${index}-child-${childIndex}`"
                        :value="openGroups[`${index}-${childIndex}`]"
                        @input="(val) => $set(openGroups, `${index}-${childIndex}`, val)"
                        no-action
                        sub-group
                        @click="child.onClick ? child.onClick() : null"
                      >
                        <template v-slot:activator>
                          <v-list-item-icon v-if="child.icon">
                            <v-icon>{{ child.icon }}</v-icon>
                          </v-list-item-icon>

                          <v-list-item-content style="position: relative;">
                            <v-list-item-title class="text-truncate" style="padding-right: 28px;">
                              {{ child.label }}
                            </v-list-item-title>
                            <v-badge
                              v-if="child.badge && !openGroups[`${index}-${childIndex}`]"
                              inline
                              color="red"
                              :content="child.badge.content"
                              :value="child.badge.value"
                              style="position: absolute; top: 0; right: 0;"
                            />
                          </v-list-item-content>
                        </template>

                        <v-list-item
                          v-for="(subChild, subIndex) in child.children"
                          :key="`main-${index}-child-${childIndex}-sub-${subIndex}`"
                          :to="subChild.to"
                          :class="subChild.className"
                        >
                          <v-list-item-icon v-if="subChild.icon">
                            <v-icon>{{ subChild.icon }}</v-icon>
                          </v-list-item-icon>

                          <v-list-item-content style="position: relative;">
                            <v-list-item-title class="text-truncate" style="padding-right: 28px;">
                              {{ subChild.label }}
                            </v-list-item-title>
                            <v-badge
                              v-if="subChild.badge"
                              inline
                              color="red"
                              :content="subChild.badge.content"
                              :value="subChild.badge.value"
                              style="position: absolute; top: 0; right: 0;"
                            />
                          </v-list-item-content>
                        </v-list-item>
                      </v-list-group>

                      <v-list-item
                        v-else-if="child.badge"
                        :key="`main-${index}-child-${childIndex}`"
                        :to="child.to"
                      >
                        <v-list-item-icon v-if="child.icon">
                          <v-icon>{{ child.icon }}</v-icon>
                        </v-list-item-icon>

                        <v-list-item-content style="position: relative;">
                          <v-list-item-title class="text-truncate" style="padding-right: 28px;">
                            {{ child.label }}
                          </v-list-item-title>
                          <v-badge
                            inline
                            color="red"
                            :content="child.badge.content"
                            :value="child.badge.value"
                            style="position: absolute; top: 0; right: 0;"
                          />
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item
                        v-else
                        :key="`main-${index}-child-${childIndex}`"
                        :to="child.to"
                      >
                        <v-list-item-icon v-if="child.icon">
                          <v-icon>{{ child.icon }}</v-icon>
                        </v-list-item-icon>

                        <v-list-item-content>
                          <v-list-item-title>
                            {{ child.label }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>

                    </template>
                  </v-list-group>
                </v-list>

                <v-list
                  v-else-if="item.badge"
                  :key="`main-list-badge-${index}`"
                  nav
                  dense
                  flat
                  class="pa-0 border-0 no-border-list"
                >
                  <v-list-item :to="item.to">
                    <v-list-item-icon v-if="item.icon">
                      <v-icon>{{ item.icon }}</v-icon>
                    </v-list-item-icon>

                    <v-list-item-content style="position: relative;">
                      <v-list-item-title class="text-truncate" style="padding-right: 28px;">
                        {{ item.label }}
                      </v-list-item-title>
                      <v-badge
                        inline
                        color="red"
                        :content="item.badge.content"
                        :value="item.badge.value"
                        style="position: absolute; top: 0; right: 0;"
                      />
                    </v-list-item-content>
                  </v-list-item>
                </v-list>

                <v-list
                  v-else
                  :key="`main-list-item-${index}`"
                  nav
                  dense
                  flat
                  class="pa-0 border-0 no-border-list"
                >
                  <v-list-item :to="item.to">
                    <v-list-item-icon v-if="item.icon">
                      <v-icon>{{ item.icon }}</v-icon>
                    </v-list-item-icon>

                    <v-list-item-content>
                      <v-list-item-title>
                        {{ item.label }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>

              </template>
            </div>
          </template>

          <!-- Company menu -->
          <template
            v-if="isLoggedIn && isRegistered && isCompanyUser(0)"
          >
            <template
              v-for="(employment, empIndex) in currentUser.employments
                ? currentUser.employments
                : []"
            >
              <div
                class="pl-4 companyname pb-4"
                :key="`company-${empIndex}-name`"
              >
                {{
                  employment.company
                    ? employment.company.name
                    : $t('fields.the-company')
                }}
              </div>

              <template
                v-for="(menuItem, itemIndex) in getCompanyMenuItems(employment)"
              >

                <v-list
                  v-if="menuItem.children"
                  :key="`company-${empIndex}-list-${itemIndex}`"
                  nav
                  dense
                  flat
                  class="pa-0 border-0 no-border-list"
                >
                  <v-list-group
                    v-model="menuItem.isOpen"
                    no-action
                    @click="
                      menuItem.onClick
                        ? menuItem.onClick()
                        : null
                    "
                  >
                    <template v-slot:activator>
                      <v-list-item-content>
                        <div class="d-flex align-start">

                          <v-list-item-icon v-if="menuItem.icon">
                            <v-icon>{{ menuItem.icon }}</v-icon>
                          </v-list-item-icon>

                          <v-list-item-title>
                            {{ menuItem.label }}
                          </v-list-item-title>

                          <v-badge
                            v-if="menuItem.badge && !menuItem.isOpen"
                            :value="menuItem.badge"
                            :content="menuItem.badge"
                            inline
                            color="black"
                          />
                        </div>
                      </v-list-item-content>
                    </template>

                    <template
                      v-for="(child, childIndex) in menuItem.children"
                    >

                      <v-list-group
                        v-if="child.subGroup && child.children"
                        :key="`company-${empIndex}-item-${itemIndex}-child-${childIndex}`"
                        no-action
                        sub-group
                        v-model="menuItem.isOpen"
                      >
                        <template v-slot:activator>
                          <v-list-item-icon v-if="child.icon">
                            <v-icon>{{ child.icon }}</v-icon>
                          </v-list-item-icon>

                          <v-list-item-content>
                            <div class="d-flex align-start">
                              <v-list-item-title>
                                {{ child.label }}
                              </v-list-item-title>

                              <v-badge
                                v-if="child.badge && !menuItem.isOpen"
                                :value="child.badge"
                                :content="child.badge"
                                inline
                                color="black"
                              />
                            </div>
                          </v-list-item-content>
                        </template>

                        <v-list-item
                          v-for="(subChild, subIndex) in child.children"
                          :key="`company-${empIndex}-item-${itemIndex}-child-${childIndex}-sub-${subIndex}`"
                          :to="subChild.to"
                          :class="subChild.className"
                        >
                          <v-list-item-icon v-if="subChild.icon">
                            <v-icon>{{ subChild.icon }}</v-icon>
                          </v-list-item-icon>

                          <v-list-item-content>
                            <v-list-item-title>
                              {{ subChild.label }}
                            </v-list-item-title>
                          </v-list-item-content>

                          <v-badge
                            v-if="subChild.badge"
                            :value="subChild.badge"
                            :content="subChild.badge"
                            inline
                            color="black"
                          />
                        </v-list-item>
                      </v-list-group>

                      <v-list-item
                        v-else
                        :key="`company-${empIndex}-item-${itemIndex}-child-${childIndex}-item`"
                        :to="child.to"
                        class="group-list-item"
                      >
                        <v-list-item-icon v-if="child.icon">
                          <v-icon>{{ child.icon }}</v-icon>
                        </v-list-item-icon>

                        <v-list-item-content>
                          <v-list-item-title>
                            {{ child.label }}
                          </v-list-item-title>
                        </v-list-item-content>

                        <v-badge
                          v-if="child.badge"
                          :value="child.badge"
                          :content="child.badge"
                          inline
                          color="black"
                        />
                      </v-list-item>

                    </template>
                  </v-list-group>
                </v-list>

                <v-list
                  v-else
                  :key="`company-${empIndex}-item-${itemIndex}-item-list`"
                  nav
                  dense
                  flat
                  class="pa-0 border-0 no-border-list"
                >
                  <v-list-item
                    :to="menuItem.to"
                    :class="menuItem.className"
                  >
                    <v-list-item-icon v-if="menuItem.icon">
                      <v-icon>{{ menuItem.icon }}</v-icon>
                    </v-list-item-icon>

                    <v-list-item-content>
                      <v-list-item-title>
                        {{ menuItem.label }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>

              </template>
            </template>
          </template>

          <v-divider class="my-2"></v-divider>

          <!-- Help -->
          <v-list-group>
            <template v-slot:activator>
              <v-list-item-icon>
                <v-icon>$sl-question</v-icon>
              </v-list-item-icon>

              <v-list-item-content>
                <v-list-item-title>
                  {{ $t('menu.help') }}
                </v-list-item-title>
              </v-list-item-content>
            </template>

            <v-list-item :to="{ name: 'ReleaseNotes' }">
              <v-list-item-icon>
                <v-badge
                  :value="hasUnseenReleaseNotes"
                  content="!"
                  overlap
                  right
                  bordered
                >
                  <v-icon>mdi-history</v-icon>
                </v-badge>
              </v-list-item-icon>

              <v-list-item-content>
                <v-list-item-title>
                  {{ $t('menu.release-notes') }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <!-- External links -->
            <v-list-item
              v-for="(link, index) in externalLinks"
              :key="index"
              :href="link.href"
              :target="link.target"
              :title="link.title"
            >
              <v-list-item-icon>
                <v-icon>{{ link.icon }}</v-icon>
              </v-list-item-icon>

              <v-list-item-content>
                <v-list-item-title>
                  {{ link.label }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <!-- Guide -->
          <template v-if="isLoggedIn">
            <v-list-item
              v-if="
                isInstructor(0) ||
                isAdministrator ||
                isDepartmentAdministrator(0)
              "
              :to="{ name: 'Guide' }"
            >
              <v-list-item-icon>
                <v-icon>$sl-guide</v-icon>
              </v-list-item-icon>

              <v-list-item-content>
                <v-list-item-title>
                  {{ $t('tour.menu') }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>

            <!-- Help -->
            <v-list-item @click="helpClick()" class="my-1">
              <v-list-item-icon>
                <v-icon>$sl-question</v-icon>
              </v-list-item-icon>

              <v-list-item-content>
                <v-list-item-title>
                  {{ $t('menu.help') }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-group>

        </v-list>

      </div>

      <!-- Logout pinned to bottom -->
      <v-list nav dense class="mainmenu logout-menu flex-shrink-0">
        <v-list-item @click="showLogoutModal = true">
          <v-list-item-icon>
            <v-icon>$sl-logout</v-icon>
          </v-list-item-icon>

          <v-list-item-content>
            <v-list-item-title>
              {{ $t('menu.logout') }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <p v-if="appVersion" class="caption text-center mb-2" v-html="appVersion" />

    </v-container>

    <ModalBase
      :show="showLogoutModal"
      maxWidth="400px"
      :title="$t('menu.logout-title')"
      cancelIcon="$sl-cancel"
      submitIcon="$sl-logout"
      :cancelText="$t('labels.cancel')"
      :submitText="$t('menu.logout-btn')"
      @close="showLogoutModal = false"
      @cancel="showLogoutModal = false"
      @submit="logoutClick"
    />

  </v-layout>
</template>

<script>
import {mapGetters} from 'vuex'
import LocaleChanger from '@/components/Common/LocaleChanger'
import {CompanyRoles} from '@/components/mixins'
import ModalBase from '@/components/Common/ModalBase'
import get from 'lodash/get'
const SugarDate = require('sugar/date')
import API from '@/services/api'
import { bus } from '@/utils/bus'
import { find } from 'lodash'

// @group Common
// Provides the application menu
//
// Will display a logged out, instructor or apprentice menu based on currentUser
// @vuese
export default {
  name: 'AppMenu',

  mixins: [CompanyRoles],

  components: {
    LocaleChanger,
    ModalBase
  },

  data() {
    return {
      showLogoutModal: false,
      apprenticesWithBasicEducation: null,
      qualificationFinishedCount: null,
      releaseNotes: [],
      unhandledOrders: {},
      unhandledApplications: {},
      openGroups: {}
    }
  },

  created() {
    this.qualificationFinishedCount = null;

    ['to-confirm','to-approve-for-company'].forEach(event => {
      bus.$on(event, payload => this.setBadges(event, payload));
    })
  },

  beforeDestroy() {
    ['to-confirm','to-approve-for-company'].forEach(event => {
      bus.$off(event, this.setBadges);
    })
  },

  watch: {
    '$route.name'(newName, oldName) {
      if (oldName === 'ApprenticeQualifyingTime' && this.isLoggedIn) {
        this.setQualifyingApprovalCount()
      }
    },

    currentUser: {
      immediate: true,

      handler() {
        if (!this.isLoggedIn) return

        this.fetchApprenticeBasicEducation()
        this.setQualifyingApprovalCount()
        this.listReleaseNotes()
      }
    }
  },

  computed: {
    ...mapGetters('currentuser', [
      'currentUser',
      'isRegistered',
      'isLoggedIn',
      'isApprentice',
      'isCompanyUser',
      'isInstructor',
      'isAdministrator',
      'isCompanyEducationResponsible',
      'isDistanceEducationAdministrator',
      'isDepartmentAdministrator',
      'hasTradeCertificates'
    ]),
    ...mapGetters('app', [
      'apiVersion',
    ]),
    
    hasQualificationTime() {
      return Boolean(
        this.currentUser.educations.find(
          e =>
            (e.qualificationTime != null &&
            SugarDate.Date.isBetween(new Date(), e.qualificationTime.fromDate, e.qualificationTime.toDate))
              ||
            (e.qualificationTime != null &&
            e.qualificationTime.isCancelled)
        )
      )
    },

    isActiveInstructor() {
      return this.isInstructor(0) && this.currentUser?.employments?.some(e => e.apprenticeshipInstructors?.some(a => a.isActive))
    },

    formattedQualificationFinishedCount() {
      return `${ this.qualificationFinishedCount > 9 ? 9 + '+' : this.qualificationFinishedCount }`
    },

    // Profile menu items
    profileMenuItems() {
      const items = [
        { to: {name: 'PersonAddress'}, icon: '$sl-my-address', label: this.$t('labels.my-address') },
        { to: {name: 'PersonSettings'}, icon: '$sl-settings', label: this.$t('labels.my-settings') }
      ]
      
      if (this.isInstructor(0) || this.isApprentice || this.hasTradeCertificates) {
        items.push({ 
          to: {name: 'PersonQualifications'}, 
          icon: '$sl-trade-certificate', 
          label: this.$t('labels.qualifications') 
        })
      }
      
      if (!this.isCompanyUser(0)) {
        items.push(
          { 
            label: this.$t('labels.application'),
            to: { name: 'ApplicationStart', params: { setStep: 0 }},
            icon: '$sl-apprentice-application'
          },
          { to: {name: 'ApplicationStart'}, icon: '$sl-add', label: this.$t('labels.additional-occupation') },
          { to: {name: 'ApplicationStart'}, icon: '$sl-change-company', label: this.$t('labels.change-company') }
        )
      }
      
      return items
    },

    // Apprentice books
    apprenticeBooksItems() {
      if (!this.currentUser.books || this.isInstructor(0)) {
        return []
      }
      
      return this.currentUser.books.map(book => ({
        to: {name: 'EbookStart', params: {id: book.id}},
        icon: '$sl-ebook',
        label: this.currentUser.books.length > 1 
          ? this.$t('ebook.button-label') + ' ' + this.$options.filters.date(book.createdAt)
          : this.$t('labels.my-book')
      }))
    },

    // External links at the bottom of menu
    externalLinks() {
      return [
        { 
          href: 'mailto:support@byn.se', 
          icon: '$sl-send', 
          label: this.$t('menu.contact-support'),
          target: '_blank'
        },
        { 
          href: 'https://www.byggbranschensyrkesnamnd.se/wp-content/uploads/2026/07/Manual-Mina-Sidor.docx', 
          icon: '$sl-manual', 
          label: this.$t('menu.manual'),
          target: '_blank'
        },
        { 
          href: 'https://www.byggbranschensyrkesnamnd.se/wp-content/uploads/2024/11/Mall-utvecklingsplan-2406.docx', 
          icon: '$sl-write', 
          label: this.$t('menu.development-plan'),
          target: '_blank'
        },
        { 
          href: 'https://www.byggbranschensyrkesnamnd.se/wp-content/uploads/2024/05/Franvaroberakning_kvalificeringstid.xlsx', 
          icon: '$sl-task-list-approve', 
          label: this.$t('menu.absence-calculator-qualifying-time'),
          target: '_blank',
          title: 'https://www.byggbranschensyrkesnamnd.se/wp-content/uploads/2024/05/Franvaroberakning_kvalificeringstid.xlsx'
        },
        { 
          href: 'https://www.byggbranschensyrkesnamnd.se/wp-content/uploads/2024/11/Mall-tjanstgoringsintyg-2406.docx', 
          icon: '$sl-trade-certificate', 
          label: this.$t('menu.service-certificate'),
          target: '_blank'
        }
      ]
    },

    myApprenticesMenuItems() {
      const items = [];

      // Apprentice ebooks
      if (this.isInstructor(0) || (this.isCompanyUser(0) && this.apprenticesWithBasicEducation > 0)) {
        items.push({
          to: {name: 'ApprenticeEbooks'},
          icon: '$sl-ebook',
          label: this.$t('labels.apprentice-books')
        })
      }
      if (this.isCompanyUser(0)) {
        const badge = this.formattedQualificationFinishedCount.length
          ? { content: this.formattedQualificationFinishedCount, value: this.qualificationFinishedCount }
          : null;
        items.push({
          to: {name: 'ApprenticeQualifyingTime'},
          icon: '$sl-stairs-ascend',
          label: this.$t('labels.apprentice-qualifying-time'),
          badge
        })

        // Basic education time
        if (this.apprenticesWithBasicEducation > 0) {
          items.push({
            to: {name: 'PersonBasicEducationTime'},
            icon: '$sl-owl',
            label: this.$t('labels.e-book-label-secondary')
          })
        }
      }

      return items
    },

    // Main menu items (everything before companies section)
    mainMenuItems() {
      const items = []

      // Home
      items.push({
        to: '/',
        icon: '$sl-home',
        label: this.$t('menu.home')
      })

      if (!this.isLoggedIn || !this.isRegistered || !this.currentUser) {
        return items
      }

      // My profile group
      if(this.profileMenuItems.length){
        items.push({
          icon: '$sl-user',
          label: this.$t('labels.my-profile'),
          group: true,
          children: this.profileMenuItems
        })
      }

      if(!this.myApprenticesMenuItems.length && this.isInstructor(0) && this.isActiveInstructor){
        items.push({ 
          to: {name: 'CompanyInstructorApprentices', params: {id: this.currentUser.id}}, 
          icon: '$sl-apprentice', 
          label: this.$t('labels.my-apprentice') 
        })
      }

      if(this.myApprenticesMenuItems.length){
      const badge = this.formattedQualificationFinishedCount.length
          ? { content: this.formattedQualificationFinishedCount, value: this.qualificationFinishedCount }
          : null;
        items.push({
          icon: '$sl-apprentices',
          label: this.$t('labels.my-apprentice'),
          group: true,
          children: this.myApprenticesMenuItems,
          onClick: this.isInstructor(0) && this.isActiveInstructor
            ? () => this.$router.push({name: 'CompanyInstructorApprentices', params: {id: this.currentUser.id}}).catch(() => {})
            : null,
            badge
        })
      }

      // Order card and books for apprentice
      if ((this.isApprentice && this.currentUser.books) || this.hasTradeCertificates) {
        items.push({
          to: {name: 'MyOrderCardStart'},
          icon: '$sl-invitation',
          label: this.$t('labels.person-orders-machine')
        })
      }

      // Instructor menu items
      if (this.isInstructor(0)) {
        items.push({
          to: {name: 'InstructorInstructions'},
          icon: '$sl-book-flip',
          label: this.$t('labels.instructor-instruction')
        })

        if (this.currentUser.books && this.currentUser.books.length) {
          items.push({ 
            to: {name: 'InstructorEbooks', params: {id: this.currentUser.id}}, 
            icon: '$sl-ebook', 
            label: this.$t('labels.my-book') 
          })
        }
      }

      // My qualifying time
      if (this.get(this.currentUser, 'educations', []).some(o => o.qualificationTime)) {
        items.push({
          to: {name: 'PersonQualifyingTime', params: {id: this.currentUser.id}},
          icon: '$sl-stairs-ascend',
          label: this.$t('labels.my-qualifying-time')
        })
      }

      // Company users section
      if (this.isCompanyUser(0)) {
        // My book for non-instructors
        if (this.isInstructor(0) === false &&
            (this.isCompanyEducationResponsible(0) ||
             this.isDistanceEducationAdministrator(0) ||
             this.isDepartmentAdministrator(0) ||
             this.isAdministrator) && 
            this.currentUser.books.length) {
          items.push({
            to: {name: 'InstructorEbooks', params: {id: this.currentUser.id}},
            icon: '$sl-ebook',
            label: this.$t('labels.my-book')
          })
        }
      }

      // Apprentice books
      this.apprenticeBooksItems.forEach(book => items.push(book))

      return items
    },

    hasUnseenReleaseNotes() {
      const lastSeenId = get(this, 'currentUser.lastSeenMinasidorReleaseNoteId', null)

      const notesForApp = this.releaseNotes.filter(
        note => note.status === 'published'
      )

      return notesForApp.some(note =>
        lastSeenId == null || note.id > lastSeenId
      )
    },

    appVersion() {
      return `Version ${process.env.VUE_APP_VERSION} &middot; API ${this.apiVersion}`
    }
  },

  methods: {
    get,

    // Generates menu items for a specific company
    getCompanyMenuItems(employment) {
      const companyId = employment.companyId
      const items = []

      // Company settings
      const settingsGroup = {
        icon: '$sl-settings',
        label: this.$t('labels.company-settings'),
        children: []
      }

      if (this.isCompanyUser(companyId)) {
        settingsGroup.children.push({
          to: {name: 'CompanyWorkplaces', params: {id: companyId}},
          icon: '$sl-workplace',
          label: this.$t('labels.workplaces')
        })
      }

      if ((this.isAdministrator || this.isDepartmentAdministrator(companyId)) && this.hasDepartments(companyId)) {
        settingsGroup.children.push({
          to: {name: 'CompanyDepartments', params: {id: companyId}},
          icon: '$sl-departments',
          label: this.$t('company.sections')
        })
      }

      if (settingsGroup.children.length) {
        items.push(settingsGroup)
      }

      // Staff
      const staffGroup = {
        icon: '$sl-staff',
        label: this.$t('labels.staff'),
        children: [],
        onClick: (this.isAdministrator || this.isDepartmentAdministrator(companyId) || this.isCompanyEducationResponsible(companyId)) 
          ? () => this.$router.push({name: 'CompanyEmployees', params: {id: companyId}}).catch(() => {})
          : null
      }

      if (this.isDepartmentAdministrator(companyId) || this.isCompanyEducationResponsible(companyId) || this.isAdministrator) {
        staffGroup.children.push({
          to: {name: 'CompanyEmployeesPermissions', params: {id: companyId}},
          icon: '$sl-permissions',
          label: this.$t('labels.roles'),
          className: 'subgroup-list-item'
        })
      }

      if (this.isDistanceEducationAdministrator(companyId) || this.isDepartmentAdministrator(companyId) || this.isAdministrator) {
        staffGroup.children.push({
          to: {name: 'CompanyInstructors', params: {id: companyId}},
          icon: '$sl-instructor',
          label: this.$t('labels.instructors'),
          className: 'subgroup-list-item'
        })
      }

      if (this.isDepartmentAdministrator(companyId) || this.isDistanceEducationAdministrator(companyId) || this.isAdministrator) {
        staffGroup.children.push({
          to: {name: 'CompanyExemptions', params: {id: companyId}},
          icon: '$sl-single-neutral-actions-check',
          label: this.$t('labels.exemption')
        })
      }

      if (this.isCompanyEducationResponsible(companyId) || this.isDistanceEducationAdministrator(companyId) || 
          this.isDepartmentAdministrator(companyId) || this.isAdministrator) {
        staffGroup.children.push({
          to: {name: 'CompanyYUA', params: {id: companyId}},
          icon: '$sl-yua',
          label: this.$t('labels.yua'),
          className: 'subgroup-list-item'
        })
      }

      if (staffGroup.children.length || staffGroup.onClick) {
        items.push(staffGroup)
      }

      // Apprentices
      const apprenticesGroup = {
        icon: '$sl-apprentice',
        label: this.$t('labels.apprentices'),
        children: [],
        badge: this.unhandledApplications[companyId] + this.unhandledOrders[companyId]
      }

      if (this.isCompanyEducationResponsible(companyId) || this.isDistanceEducationAdministrator(companyId) ||
          this.isDepartmentAdministrator(companyId) || this.isAdministrator) {
        apprenticesGroup.children.push({
          to: {name: 'CompanyApprenticesOverview', params: {id: companyId}},
          icon: '$sl-apprentices',
          label: this.$t('labels.apprentices_overview')
        })

        apprenticesGroup.children.push({
          to: {name: 'CompanyApprentices', params: {id: companyId}},
          icon: '$sl-apprentice',
          label: this.$t('labels.apprentices_all_btn')
        })
        
        apprenticesGroup.children.push({
          to: {name: 'CompanyApplications', params: {id: companyId}},
          icon: '$sl-apprentice-application',
          label: this.$t('labels.applications'),
          badge: this.unhandledApplications[companyId]
        })
      }

      // Orders subgroup
      const ordersSubGroup = {
        // icon: '$sl-approve-distance-order',
        label: this.$t('labels.distance-orders'),
        subGroup: true,
        children: [],
        badge: this.unhandledOrders[companyId]
      }

      if (this.isDistanceEducationAdministrator(companyId) || this.isDepartmentAdministrator(companyId) || this.isAdministrator) {
        ordersSubGroup.children.push({
          to: {name: 'CompanyDistanceOrders', params: {companyId}},
          icon: '$sl-approve-distance-order',
          label: this.$t('labels.distance-orders-type.basic-education'),
          className: 'subgroup-list-item',
          badge: this.unhandledOrders[companyId]
        })
      }

      if (this.isCompanyUser(companyId)) {
        ordersSubGroup.children.push({
          to: {name: 'CompanyOrderCardStart', params: {id: companyId}},
          icon: '$sl-invitation',
          label: this.$t('labels.person-orders-machine'),
          className: 'subgroup-list-item'
        })
      }

      // Add apprentice application
      if (this.isCompanyUser(companyId)) {
        apprenticesGroup.children.push({
          to: {name: 'CreateCompanyApplications', params: {id: companyId, setStep: 0}},
          icon: '$sl-add-application',
          label: this.$t('labels.add-new-applications')
        })
      }

      // Pause
      if (this.isAdministrator || this.isDepartmentAdministrator(companyId) || this.isCompanyEducationResponsible(companyId)) {
        apprenticesGroup.children.push({
          to: {name: 'CompanyPausedPersons', params: {id: companyId}},
          icon: '$sl-pause',
          label: this.$t('labels.pause')
        })
      }

      if (ordersSubGroup.children.length) {
        apprenticesGroup.children.push(ordersSubGroup)
      }

      if (apprenticesGroup.children.length) {
        items.push(apprenticesGroup)
      }

      return items
    },

    async setQualifyingApprovalCount() {
      try {

        const { data } = await API.getApprenticesQualifyingTimes()

        const apprentices = (data || [])
          .filter(({ personalNumber }) => personalNumber && personalNumber !== get(this, 'currentUser.personalNumber', null))

        const qualificationTimes = apprentices.flatMap(({ educations }) => 
          (educations || []).map(education => get(education, 'qualificationTime', null)))

        this.qualificationFinishedCount = qualificationTimes.reduce((sum, qt) => {
          const endDateHasPassed = SugarDate.Date(qt.toDate).isBefore().raw
          const isActive = !qt.doAbort && !qt.isCancelled && !qt.isApprovedByInstructor
          return sum + (endDateHasPassed && isActive ? 1 : 0)
        }, 0)
      } catch (error) {
        console.log('An error has occured:', error)
      }
    },

    fetchApprenticeBasicEducation() {
      if ( this.isLoggedIn ) {
        API.listBooks().then(({data = []}) => {
          const filteredApprenticies = data.filter(d => d.personId !== this.currentUser.id)
          this.apprenticesWithBasicEducation = filteredApprenticies.length
        })
        .catch(error => {
          console.log('Error getting apprenticies QT:', error)
        })
      }
    },
    
    openFeedback() {
      if (window.Userback) {
        window.Userback.open()
      }
    },
    helpClick() { 
      window.open('https://www.byggbranschensyrkesnamnd.se/manual/kategorier/mina-sidor/', '_blank')
    },
    getCompanies() {
      if (!this.currentUser) {
        return
      }
      var ret = this.currentUser.employments.map(o => (o.company ? o.company.name : '')).join(', ')
      return ret
    },

    logoutClick() {
      this.showLogoutModal = false
      this.$emit('logout')
    },

    async listReleaseNotes() {
      try {
        const res = await API.listReleaseNotes()
        this.releaseNotes = res.data || []
      } catch (error) {
        console.log(error);
      }
    },

    setBadges(event, payload) {
      const employments = get(this, 'currentUser.employments', []);

      const result = {};

      employments.forEach(({ companyId }) => {
        const value = payload?.[companyId];

        if (Array.isArray(value)) {
          result[companyId] = value.length;
        } else if (typeof value === 'number') {
          result[companyId] = value;
        } else {
          result[companyId] = 0;
        }
      });

      if (event === 'to-confirm') {
        this.unhandledOrders = result;
      } else if (event === 'to-approve-for-company') {
        this.unhandledApplications = result;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.theme--light .v-list.mainmenu {
  ::v-deep .v-list-group{
    .v-list-group__header__append-icon{
      min-width: 24px;
    }
    .v-list-group__items{
      .v-list-item{
        padding-left: 36px !important;

      }
    }
    .v-list-group--sub-group{
      .v-list-group__items{
        .v-list-item{
          padding-left: 48px !important;
        }
      }
      .v-list-group__header{
        padding-left: 36px !important;
      }
    }
  }
}

div.v-list.mainmenu.v-sheet.v-sheet--tile.theme--light {
  .v-list-item {
    padding-left: 16px !important;
  }
  .companyname {
    font-family: $body-font-family !important;
    letter-spacing: 0.15px;
    font-size: 16px !important;
    margin-top: 13px !important;
    margin-bottom: 14px !important;
    color: var(--v-primary-darken5) !important;
    font-weight: 500 !important;
  }
}

.user-details {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 56px;
  padding: 16px;
  border-radius: 16px;
  margin: 24px 0;
  background-color: $color-secondary-container !important;

  &__icon {
    width: 16px !important;
    fill: $color-primary-light !important;
  }

  &__name {
    font-size: 14px !important;
    font-weight: 600 !important;
    line-height: 20px !important;
    letter-spacing: 0.1px !important;
    color: $color-primary-light !important;
    margin: unset !important;
    font-family: $body-font-family !important;
  }
}

.side-menu-content {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.logout-menu {
  flex: 0 0 auto;
  padding: 0 !important;
  margin-top: auto;
  margin-bottom: .5rem !important;
  box-shadow: none !important;
}
</style>
